// 3rd party
import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { flowResult } from 'mobx';
import { CancellablePromise } from 'mobx/dist/api/flow';
import { observer } from 'mobx-react';

// Shared UI
import { LocalizedText } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';

// Components
import { ConfiguredQuicklinkV2 } from 'components/quicklinks';

// Hooks
import { useStores } from 'hooks';

// Models
import { LinkAnalyticsMicroMessageContent } from 'models/analytics';

// Utils
import {
  analyticsEvent,
  getLinkAnalyticsMicroMessage,
  getProductAnalyticsMicroMessage,
  getProgramAnalyticsMicroMessage,
  getUserAnalyticsMicroMessage,
} from '../../util/analytics';

const ConfiguredQuicklinksV2 = observer(
  // tslint:disable-next-line:no-shadowed-variable
  function ConfiguredQuicklinksV2(): JSX.Element {
    const { analytics, productsV2, pzStore, quicklinks, user, userAuth } = useStores();

    const { activeProduct } = productsV2;
    const { pzPublisher } = pzStore;
    const { configuredQuicklinks } = quicklinks;
    const { activeProgram } = user;
    const { currentUserDetails } = userAuth;

    const result = useRef<CancellablePromise<unknown>>();

    const analyticsTrackEvents = useCallback(
      // tslint:disable-next-line:no-shadowed-variable
      function analyticsTrackEvents(rfrr: string, link?: LinkAnalyticsMicroMessageContent) {
        const microMessages = [
          getProgramAnalyticsMicroMessage(activeProgram, activeProgram?.programStatus),
          getProductAnalyticsMicroMessage(activeProduct),
        ];
        const microMessagesWithLink = [...microMessages, getLinkAnalyticsMicroMessage(link)];

        const filterAnalyticsTrackEvent = {
          'affhub.product_config': () => {
            analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Product Config Presented', microMessages);
          },
          'affhub_copy_code.selected': () => {
            analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Copy Code Selected', microMessagesWithLink);
          },
          'affhub_copy_link.selected': () => {
            analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Copy Link Selected', microMessagesWithLink);
          },
        };

        filterAnalyticsTrackEvent[rfrr]();
      },
      [analytics, activeProgram, activeProduct, currentUserDetails, pzPublisher],
    );

    useEffect(
      function onMount() {
        result.current = flowResult(quicklinks.fetchConfiguredQuicklinks(activeProduct, activeProgram));

        return function onUnmount() {
          result.current?.cancel();
          quicklinks.resetConfiguredQuicklinks();
        };
      },
      [quicklinks, activeProduct, activeProgram],
    );

    return (
      (configuredQuicklinks && (
        <>
          <UitkSpacing margin={{ blockend: 'one' }}>
            <UitkHeading tag="h2" size={4}>
              <LocalizedText message="product.quicklink.configured.title.get_pre_configured_link" />
            </UitkHeading>
          </UitkSpacing>

          <UitkSpacing margin={{ blockend: 'six' }}>
            <UitkText>
              <LocalizedText
                message="product.quicklink.configured.content.copy_link_for_popular_pages"
                data={[activeProgram?.title]}
              />
            </UitkText>
          </UitkSpacing>

          {configuredQuicklinks.map((q, i) => (
            <ConfiguredQuicklinkV2
              key={`ql-${q.label}`}
              index={i + 1}
              label={q.label}
              title={q.title}
              text={q.text}
              url={q.url}
              link={q.link}
              code={q.code}
              analyticsTrackEvents={analyticsTrackEvents}
            />
          ))}
        </>
      )) ||
      null
    );
  },
);

export { ConfiguredQuicklinksV2 };
